import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';

import { SelectedPosition } from '../types';

interface PositionCount {
  selected: number;
  total: number;
  supplierWarning: boolean;
}

const positionCountSubject = new BehaviorSubject<PositionCount>({
  selected: 0,
  total: 0,
  supplierWarning: false,
});

// Function to update position count
export const updatePositionCount = (
  selectedPositions: SelectedPosition[],
  passThroughEnabled: boolean
) => {
  const groupedBySupplier = groupBy(
    selectedPositions,
    (x) => x.position.supplierId || x.position.supplier?.id
  );

  // Filter out all undefined supliers
  const groupBySupplierWithSupplierId = Object.entries(
    groupedBySupplier
  ).filter(([key]) => key !== 'undefined' && key !== '0');
  const selectedPositionsWithSuppliers = groupBySupplierWithSupplierId.flatMap(
    (x) => x[1]
  );

  const selected = selectedPositionsWithSuppliers.reduce((sum, position) => {
    if (position.selected) {
      if (!position.childsOriginalData) return sum + 1;

      if (!passThroughEnabled) {
        return sum + (position.position.childs?.length || 0);
      }
      const childCount =
        position.childsOriginalData?.filter((x) => x.selected).length || 0;
      return sum + childCount;
    }
    return sum;
  }, 0);

  const total = Object.entries(groupedBySupplier).filter(
    ([key, values]) =>
      key !== 'undefined' && key !== '0' && values.some((x) => x.selected)
  ).length;

  const supplierWarning = selectedPositions.some(
    (x) => x.selected && !x.position.supplier?.id
  );

  const newState = {
    selected,
    total,
    supplierWarning,
  };
  positionCountSubject.next(newState);
};

// Hook to use position count state
export const usePositionCount = () => {
  const [state, setState] = useState<PositionCount>({
    selected: 0,
    total: 0,
    supplierWarning: false,
  });

  useEffect(() => {
    const subscription = positionCountSubject.subscribe((newState) => {
      setState((prevState) => {
        // Only update if values actually changed
        if (
          prevState.selected !== newState.selected ||
          prevState.total !== newState.total ||
          prevState.supplierWarning !== newState.supplierWarning
        ) {
          return newState;
        }
        return prevState;
      });
    });
    return () => subscription.unsubscribe();
  }, []);

  return state;
};

// Export the subject for direct access if needed
export { positionCountSubject };
